import { NextPage } from 'next'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import moment from 'moment'
moment.locale('ja')
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { setSpaceRedirectUrl } from 'lib/setSpaceRedirectUrl'

import Layout from 'components/commons/layouts/Layout'
import SearchSection from 'components/domains/top/SearchSection'
import StoreSection from 'components/domains/top/StoreSection'
import MainBannerSection from 'components/domains/top/MainBannerSection'
import UsageSection from 'components/domains/top/UsageSection'
import FeatureSection from 'components/domains/top/FeatureSection'

import LocationDisplaySection from 'components/domains/top/LocationDisplaySection'
import CostSection from 'components/domains/top/CostSection'
import ReviewSection from 'components/domains/top/ReviewSection'
import OwnerSection from 'components/domains/top/OwnerSection'
import CountrySection from 'components/domains/top/CountrySection'

import { AuthContext } from 'providers/AuthProvider'

import { useCustomIntercom } from 'hooks/common/intercom'
import { useAffiliateTracking } from 'hooks/tracking/useAffiliateTracking'
import { useCountries } from 'features/country/hooks/useCountries'

import Meta from 'lib/meta'
import { usePrefectures } from 'features/prefecture/hooks/usePrefectures'
import { JP_COUNTRY_ID } from 'features/prefecture/constants'

import styles from '../styles/index.module.scss'

const Top: NextPage<{ locale: string }> = ({ locale }): JSX.Element => {
  useAffiliateTracking()
  const { isSigned } = useContext(AuthContext)
  const { t } = useTranslation()

  const { prefectures } = usePrefectures({ countryId: JP_COUNTRY_ID })

  const { countries } = useCountries()

  //Locationまでスクロール用のDomを生成
  const refTopLocation = useRef<HTMLDivElement>()
  const scrollLocationSection = useCallback(() => {
    refTopLocation.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [refTopLocation])

  //預かり場所
  const locations = [
    {
      name: t('TOP.LOCATION.TOKYO'),
      slug: 'tokyo/1',
      lat: 35.681236,
      lon: 139.767125,
      backgroundImage: 'url("/tokyo.webp") no-repeat',
    },
    {
      name: t('TOP.LOCATION.OSAKA'),
      slug: 'osaka/20',
      lat: 34.7334658,
      lon: 135.5002547,
      backgroundImage: 'url("/osaka.webp") no-repeat',
    },
    {
      name: t('TOP.LOCATION.KYOTO'),
      slug: 'kyoto/2',
      lat: 34.985849,
      lon: 135.7587667,
      backgroundImage: 'url("/kyoto.webp") no-repeat',
    },
    {
      name: t('TOP.LOCATION.FUKUOKA'),
      slug: 'fukuoka/79',
      lat: 33.5897275,
      lon: 130.4207274,
      backgroundImage: 'url("/fukuoka.webp") no-repeat',
    },
    {
      name: t('TOP.LOCATION.NAGOYA'),
      slug: 'aichi/3',
      lat: 35.170915,
      lon: 136.8815369,
      backgroundImage: 'url("/nagoya.webp") no-repeat',
    },
    {
      name: t('TOP.LOCATION.SAPPORO'),
      slug: 'hokkaido/14',
      lat: 43.0686606,
      lon: 141.3507552,
      backgroundImage: 'url("/sapporo.webp") no-repeat',
    },
  ]

  const { openIntercomMessage } = useCustomIntercom()

  useEffect(() => {
    openIntercomMessage()
    setSpaceRedirectUrl()
  }, [])

  return (
    <Layout>
      <Meta
        title={t('META.TITLE.TOP_PAGE')}
        description={t('META.DESCRIPTION.TOP_PAGE')}
        url={`/${locale === 'default' ? 'ja' : locale}`}
      />
      <div className={styles.wrap}>
        <SearchSection locale={locale} />
        {locale === 'ja' ? <MainBannerSection locale={locale} /> : <StoreSection />}
        {!isSigned && <UsageSection style={locale === 'ja' && styles.usageSection} />}
        {!isSigned && <FeatureSection countryCode="jpn" />}
        <div id="scroll-location-section" ref={refTopLocation} />
        <LocationDisplaySection
          locations={locations}
          prefectures={prefectures ?? []}
          locale={locale}
          countryCode={'jpn'}
          onScrollLocation={scrollLocationSection}
        />
        <CostSection />
        {(locale === 'ja' || locale === 'en') && !isSigned && <ReviewSection />}
        {!isSigned && <OwnerSection />}
        {countries && countries.length > 0 && (
          <CountrySection countries={countries} locale={locale} />
        )}
      </div>
    </Layout>
  )
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale: locale.replace('-', '_').toLowerCase(),
    },
  }
}

export default Top
